import type { AnchorHTMLAttributes } from "react";
import type { Components } from "react-markdown";
import clsx from "clsx";

function isSafeLinkHref(href: string | undefined): boolean {
  if (!href) {
    return false;
  }
  const normalized = href.trim().toLowerCase();
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("mailto:")
  );
}

function MarkdownLink({
  href,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (!isSafeLinkHref(href)) {
    return <span>{children}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

const TABLE_SCROLL_WRAP_CLASS =
  "my-2 overflow-x-auto rounded-md border border-slate-200/60 bg-white/80 dark:border-slate-700/45 dark:bg-slate-900/35";

const TABLE_CLASS = "w-full min-w-max border-collapse text-left text-xs";

export const chatAssistantMarkdownComponents: Components = {
  a: MarkdownLink,
  table: ({ children }) => (
    <div className={TABLE_SCROLL_WRAP_CLASS} data-testid="chat-assistant-markdown-table">
      <table className={TABLE_CLASS}>{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="text-slate-800 dark:text-slate-200">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr
      className={clsx(
        "border-b border-slate-100/80 last:border-b-0 even:bg-slate-50/50 dark:border-slate-800/80 dark:even:bg-slate-800/20",
      )}
    >
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th
      className={clsx(
        "border-b border-slate-200/70 bg-slate-50/90 px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:border-slate-700/50 dark:bg-slate-800/50 dark:text-slate-400",
      )}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      className={clsx(
        "px-2 py-1.5 align-top break-words text-[12px] text-slate-800 dark:text-slate-100",
      )}
    >
      {children}
    </td>
  ),
};
